// Sales data access (Stage 2E-1). Talks ONLY to the sales_* tables/RPCs, the private sales-docs bucket and the signed-in
// session (who is using Sales). Never to Accounts, staff/payroll, rent or attendance. Every write is an Admin RPC (database-checked).
// Never log bank details: errors are reduced to their message.
import { supabase } from "../supabase";
import { optimizedForUpload } from "../lib/uploadCompression";
import { storeFile, fileUrl, fileBlob, isR2, registerCompanyFile, latestCompanyFile } from "../lib/fileStore";

const BUCKET = "sales-docs";

const fail = (error) => { const e = new Error(error.message || "Request failed"); e.code = error.code; throw e; };
// Writes are limited to 30 s. The database reports an edit conflict with SQLSTATE 40001, which the API layer treats as
// retryable and retries until its gateway gives up (~2 min, HTTP 504). Edits are therefore pre-checked against the current
// version (below), and a save that doesn't answer in time is reported plainly. Retrying is safe: creates carry a
// client_request_id and stale edits are always refused by the database.
const SAVE_TIMEOUT_MS = 30000;
const rpc = async (fn, args) => {
    const ctrl = new AbortController(); const timer = setTimeout(() => ctrl.abort(), SAVE_TIMEOUT_MS);
    try {
        const { data, error } = await supabase.rpc(fn, args).abortSignal(ctrl.signal);
        if (error) {
            if (ctrl.signal.aborted || /abort/i.test(error.message || "") || error.code === "20") fail({ code: "TIMEOUT", message: "timeout" });
            if (/upstream request timeout/i.test(error.message || "")) fail({ code: "TIMEOUT", message: "timeout" });
            fail(error);
        }
        return data;
    } finally { clearTimeout(timer); }
};
// Refuse an edit locally when the record changed since it was opened (avoids the 40001 retry loop described above).
const ensureVersion = async (table, id, expected) => {
    if (!id) return;
    const { data, error } = await supabase.from(table).select("version").eq("id", id).maybeSingle();   // settings: id = true
    if (error) fail(error);
    if (!data || data.version !== expected) fail({ code: "40001", message: "changed" });
};
const select = async (table, cols, order) => {
    let q = supabase.from(table).select(cols);
    for (const o of order || []) q = q.order(o, { ascending: true });
    const { data, error } = await q;
    if (error) fail(error);
    return data || [];
};

// Plain-language messages for the database's coded refusals.
export function errText(e) {
    if (!e) return "";
    const m = String(e.message || e);
    if (e.code === "40001") return "Someone else changed this in the meantime. Reload and try again.";
    if (e.code === "TIMEOUT") return "The save didn't finish in time. Reload to check whether it was saved, then try again if needed.";
    if (e.code === "INTEGRITY") return m;
    if (e.code === "42501" || /permission denied/i.test(m)) return "Sales is available to Admin only.";
    if (e.code === "23P01") return m.replace(/^POSSIBLE_DUPLICATE:\s*/, "");
    return m;
}

export const newRequestId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// ─── Load ────────────────────────────────────────────────────────────────────
const isoShift = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
export async function loadSales() {
    const [settings, series, services, clients, quotations, invoiceRows, invoiceStatus, calendar] = await Promise.all([
        select("sales_settings", "*"),
        select("sales_number_series", "doc_type, fy_label, next_no", ["fy_label", "doc_type"]),
        select("sales_services", "*", ["sort", "name"]),
        select("sales_clients", "*", ["name"]),
        select("sales_quotations", "*"),
        select("sales_invoices", "*"),
        select("sales_v_invoices", "id, payment_status, overdue, outstanding, settled"),      // derived by the database (2E-3 stores no payment state)
        loadCalendar(),
    ]);
    quotations.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    const st = Object.fromEntries(invoiceStatus.map(r => [r.id, r]));
    const invoices = invoiceRows.map(i => ({ ...i, payment_status: st[i.id]?.payment_status, overdue: !!st[i.id]?.overdue, outstanding: st[i.id]?.outstanding, settled: st[i.id]?.settled }))
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    return { settings: settings[0] || null, series, services, clients, quotations, invoices, calendar };
}

// Verified Nepali calendar months (database helper; reference data only). About two years back and one ahead.
export async function loadCalendar() {
    const { data, error } = await supabase.rpc("sales_bs_calendar", { p_from: isoShift(-730), p_to: isoShift(365) });
    if (error) fail(error);
    return (data || []).map(r => ({ ...r, verified: true }));
}

// Fiscal year of a date from the verified Nepali calendar (database helper; null when not verified yet).
export async function fiscalYearOf(isoDate) {
    try { return await rpc("sales_fy_label", { p_date: isoDate }); } catch { return null; }
}

export async function loadHistory(table, recordId) {
    const { data, error } = await supabase.from("sales_audit_log").select("at, auth_uid, action, before, after, reason")
        .eq("table_name", table).eq("record_id", String(recordId)).order("at", { ascending: true });
    if (error) fail(error);
    return data || [];
}

// The signed-in user (from the session; no staff/payroll lookup). Used to show "you" in history.
export async function currentUser() {
    const { data } = await supabase.auth.getUser();
    return data?.user ? { id: data.user.id, email: data.user.email || null } : null;
}

// ─── Writes (Admin RPCs) ─────────────────────────────────────────────────────
// Settings: partial update — only the keys passed are changed.
export const saveSettings = async (changes, expectedVersion) => {
    await ensureVersion("sales_settings", true, expectedVersion);
    return rpc("sales_update_settings", { p: changes, p_expected_version: expectedVersion });
};

// Services and clients: the RPC overwrites optional fields from the payload, so ALWAYS send the complete record.
export const saveService = async (record) => { await ensureVersion("sales_services", record.id, record.expected_version); return rpc("sales_save_service", { p: record }); };
export const saveClient = async (record) => { await ensureVersion("sales_clients", record.id, record.expected_version); return rpc("sales_save_client", { p: record }); };

// ─── Quotations (Stage 2E-2) ─────────────────────────────────────────────────
export async function loadQuotationLines(id) {
    const { data, error } = await supabase.from("sales_quotation_lines").select("*").eq("quotation_id", id).order("line_no", { ascending: true });
    if (error) fail(error);
    return data || [];
}
export async function loadQuotation(id) {
    const { data, error } = await supabase.from("sales_quotations").select("*").eq("id", id).maybeSingle();
    if (error) fail(error);
    return data;
}
// The database's totals for an unsaved draft (what saving would store). Read-only.
export const previewTotals = (draft) => rpc("sales_preview_totals", { p: draft });
// Drafts: the RPC replaces the lines from the payload, so ALWAYS send the complete draft.
export const saveQuotation = async (record) => { await ensureVersion("sales_quotations", record.id, record.expected_version); return rpc("sales_save_quotation", { p: record }); };
export const deleteQuotationDraft = (id) => rpc("sales_delete_quotation_draft", { p_id: id });
export const issueQuotation = async (q) => { await ensureVersion("sales_quotations", q.id, q.version); return rpc("sales_issue_quotation", { p_id: q.id, p_expected_version: q.version }); };
export const markQuotationSent = async (q, sentAt) => { await ensureVersion("sales_quotations", q.id, q.version); return rpc("sales_mark_quotation_sent", { p_id: q.id, p_expected_version: q.version, p_sent_at: sentAt }); };
export const setQuotationStatus = async (q, status, reason) => {
    await ensureVersion("sales_quotations", q.id, q.version);
    return rpc("sales_set_quotation_status", { p_id: q.id, p_status: status, p_expected_version: q.version, p_reason: reason || null });
};
// Accepted → a Sales invoice DRAFT (never an accounting entry). The database allows it exactly once.
export const convertQuotation = async (q, invoiceDate) => {
    await ensureVersion("sales_quotations", q.id, q.version);
    return rpc("sales_convert_quotation", { p_id: q.id, p_expected_version: q.version, p_invoice_date: invoiceDate || null });
};

export async function sha256Hex(bytes) {
    const d = await crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, "0")).join("");
}
// Store the issued copy once: upload under a new name (never overwriting), then record path + hash. A file uploaded by a
// failed earlier attempt is simply left unused; the database accepts only one stored copy per quotation.
export async function storeQuotationPdf(q, bytes) {
    const { ref, sha256 } = await storeFile({ target: "sales.quotation_pdf", ownerId: q.id, blob: bytes,
        fileName: `${q.doc_no}.pdf`, mime: "application/pdf",
        legacy: { bucket: BUCKET, path: `quotations/${q.id}/${String(q.doc_no).replace(/[^A-Za-z0-9-]+/g, "-")}-${newRequestId().slice(0, 8)}.pdf` } });
    return rpc("sales_attach_quotation_pdf", { p_id: q.id, p_path: ref, p_sha256: sha256 });
}
// The stored copy, checked against the hash recorded at issue. A mismatch is refused: the file is never shown.
export async function openStoredPdf(q) {
    if (!q.issued_pdf_path || !q.issued_pdf_sha256) { const e = new Error("This quotation has no stored copy yet."); e.code = "INTEGRITY"; throw e; }
    const data = await fileBlob(BUCKET, q.issued_pdf_path);
    const bytes = new Uint8Array(await data.arrayBuffer());
    if (await sha256Hex(bytes) !== q.issued_pdf_sha256) {
        const e = new Error("Integrity check failed: the stored copy of this quotation doesn't match the fingerprint recorded when it was issued, so it won't be opened. Don't send it; report this to the Admin.");
        e.code = "INTEGRITY"; throw e;
    }
    return bytes;
}
// A private image (logo / payment QR) as PNG bytes + size for the PDF. WebP and JPEG are redrawn as PNG in the browser.
export async function loadPdfImage(path) {
    if (!path) return null;
    const data = await fileBlob(BUCKET, path);
    const bmp = await createImageBitmap(data);
    const c = document.createElement("canvas"); c.width = bmp.width; c.height = bmp.height;
    c.getContext("2d").drawImage(bmp, 0, 0);
    const png = await new Promise(res => c.toBlob(res, "image/png"));
    return { data: new Uint8Array(await png.arrayBuffer()), format: "PNG", w: bmp.width, h: bmp.height };
}

// ─── Invoices (Stage 2E-3) ───────────────────────────────────────────────────
// Writes are Admin RPCs; totals are calculated by the database; nothing here touches Accounts.
export async function loadInvoiceLines(id) {
    const { data, error } = await supabase.from("sales_invoice_lines").select("*").eq("invoice_id", id).order("line_no", { ascending: true });
    if (error) fail(error);
    return data || [];
}
export async function loadInvoice(id) {
    const { data, error } = await supabase.from("sales_invoices").select("*").eq("id", id).maybeSingle();
    if (error) fail(error);
    return data;
}
// Drafts: the RPC replaces the lines from the payload, so ALWAYS send the complete draft.
export const saveInvoice = async (record) => { await ensureVersion("sales_invoices", record.id, record.expected_version); return rpc("sales_save_invoice", { p: record }); };
export const deleteInvoiceDraft = (id) => rpc("sales_delete_invoice_draft", { p_id: id });
export const issueInvoice = async (v) => { await ensureVersion("sales_invoices", v.id, v.version); return rpc("sales_issue_invoice", { p_id: v.id, p_expected_version: v.version }); };
export const markInvoiceSent = async (v, sentAt) => { await ensureVersion("sales_invoices", v.id, v.version); return rpc("sales_mark_invoice_sent", { p_id: v.id, p_sent_at: sentAt }); };
export const cancelInvoice = async (v, reason) => { await ensureVersion("sales_invoices", v.id, v.version); return rpc("sales_cancel_invoice", { p_id: v.id, p_expected_version: v.version, p_reason: reason }); };
// D1: one linked replacement draft for a cancelled invoice (a retried request returns the same draft).
export const createReplacementInvoice = (cancelledId, requestId) => rpc("sales_create_replacement_invoice", { p_cancelled_id: cancelledId, p_client_request_id: requestId });
// D4: withdraw a quotation-created draft; its quotation becomes cancelled (reason required).
export const withdrawConvertedDraft = (invoiceId, reason) => rpc("sales_withdraw_converted_draft", { p_invoice_id: invoiceId, p_reason: reason });
// Store the issued copy once: upload under a new name (never overwriting), then record path + hash.
export async function storeInvoicePdf(v, bytes) {
    const { ref, sha256 } = await storeFile({ target: "sales.invoice_pdf", ownerId: v.id, blob: bytes,
        fileName: `${v.doc_no}.pdf`, mime: "application/pdf",
        legacy: { bucket: BUCKET, path: `invoices/${v.id}/${String(v.doc_no).replace(/[^A-Za-z0-9-]+/g, "-")}-${newRequestId().slice(0, 8)}.pdf` } });
    return rpc("sales_attach_issued_pdf", { p_id: v.id, p_path: ref, p_sha256: sha256 });
}

// ─── Files (logo, payment QR) in the private bucket ──────────────────────────
const EXT = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" };
export async function uploadSettingsImage(kind, file) {
    if (!EXT[file.type]) throw new Error("Use a PNG, JPEG or WebP image.");
    if (file.size > 2 * 1024 * 1024) throw new Error("The image must be 2 MB or smaller.");
    const { ref } = await storeFile({ target: `sales.${kind}`, blob: file, fileName: file.name, mime: file.type,
        legacy: { bucket: BUCKET, path: `settings/${kind}-${newRequestId()}.${EXT[file.type]}` } });
    // The stamp has no Sales record (2E-5): an R2 stamp is registered as a company file. Logo / QR are registered when the
    // settings are saved with the returned reference.
    if (kind === "stamp" && isR2(ref)) await registerCompanyFile(ref, "stamp");
    return ref;
}

// Company stamp (Stage 2E-5): the newest settings/stamp-* file in the private bucket (uploaded under a new name each time;
// never overwritten). No database record: Sales data and numbering are untouched.
export async function findStampPath() {
    const { data, error } = await supabase.storage.from(BUCKET).list("settings", { search: "stamp-", sortBy: { column: "created_at", order: "desc" }, limit: 20 });
    if (error) fail(error);
    const f = (data || []).filter(o => o.name.startsWith("stamp-")).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
    // Dual read: an R2 stamp (a registered company file; none on databases without 026) wins when it is newer.
    const r2Stamp = await latestCompanyFile("stamp");
    if (r2Stamp && (!f || new Date(r2Stamp.at) > new Date(f.created_at))) return r2Stamp.ref;
    return f ? `settings/${f.name}` : null;
}
// The stamp as PNG bytes for the PDF, cropped to its visible outline (transparent padding removed) so it is sized by the stamp
// itself. Aspect ratio and transparency are kept; nothing is resampled.
export async function loadStampImage() {
    const path = await findStampPath();
    if (!path) return null;
    const data = await fileBlob(BUCKET, path);
    const bmp = await createImageBitmap(data);
    const c = document.createElement("canvas"); c.width = bmp.width; c.height = bmp.height;
    const ctx = c.getContext("2d"); ctx.drawImage(bmp, 0, 0);
    const px = ctx.getImageData(0, 0, c.width, c.height).data;
    let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
    for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) if (px[(y * c.width + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    if (x1 < 0) return null;
    const w = x1 - x0 + 1, h = y1 - y0 + 1, o = document.createElement("canvas"); o.width = w; o.height = h;
    o.getContext("2d").drawImage(c, x0, y0, w, h, 0, 0, w, h);
    const png = await new Promise(res => o.toBlob(res, "image/png"));
    return { data: new Uint8Array(await png.arrayBuffer()), format: "PNG", w, h };
}

// Short-lived link for showing a private image to the Admin (never a public URL).
export async function signedUrl(path) {
    if (!path) return null;
    try { return await fileUrl(BUCKET, path); } catch { return null; }
}

// ─── Payments and statements (Stage 2E-4) ────────────────────────────────────
// The database is the authority: it checks the date rules and the invoice balance (row-locked), assigns the receipt number
// and keeps every recorded amount immutable. Recording a payment never depends on Accounts; the Accounts sync (024) is a
// separate call made afterwards (below).
export async function loadPayments(invoiceIds) {
    if (!invoiceIds?.length) return [];
    const { data, error } = await supabase.from("sales_payments").select("*").in("invoice_id", invoiceIds)
        .order("payment_date", { ascending: true }).order("seq_no", { ascending: true });
    if (error) fail(error);
    return data || [];
}
export async function loadPayment(id) {
    const { data, error } = await supabase.from("sales_payments").select("*").eq("id", id).maybeSingle();
    if (error) fail(error);
    return data;
}
export const invoiceBalance = async (id) => { const r = await rpc("sales_invoice_balance", { p_id: id }); return (Array.isArray(r) ? r[0] : r) || null; };
// Record: ALWAYS with a client_request_id (a retry returns the same payment and receipt number).
export const recordPayment = (record) => rpc("sales_record_payment", { p: record });
export const voidPayment = (id, reason) => rpc("sales_void_payment", { p_id: id, p_reason: reason });

// Payment files (proof, TDS certificate): PDF, JPEG, PNG or WebP up to 10 MB (the bucket's limits), uploaded under a new
// name (never overwriting) in payments/<payment id>/ — no client name, amount or bank detail in the file name.
const PAY_EXT = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
export const PAYMENT_FILE_TYPES = Object.keys(PAY_EXT).join(",");
export function paymentFileError(file) {
    if (!file) return "";
    if (!PAY_EXT[file.type]) return "Use a PDF, JPEG, PNG or WebP file.";
    if (file.size > 10 * 1024 * 1024) return "The file must be 10 MB or smaller.";
    return "";
}
async function uploadPaymentFile(paymentId, kind, chosen) {
    const file = await optimizedForUpload(chosen);   // optimised proof image (or the original); never logos / QR / generated PDFs
    const bad = paymentFileError(file);
    if (bad) throw new Error(bad);
    const { ref, sha256 } = await storeFile({ target: kind === "proof" ? "sales.payment_proof" : "sales.tds_certificate", ownerId: paymentId,
        blob: file, fileName: file.name, mime: file.type,
        legacy: { bucket: BUCKET, path: `payments/${paymentId}/${kind}-${newRequestId().slice(0, 8)}.${PAY_EXT[file.type]}` } });
    return { path: ref, hash: sha256 };
}
// D5: first attach (no reason) or the single replacement (reason required; the old file stays).
export async function attachPaymentProof(paymentId, file, reason) {
    const { path, hash } = await uploadPaymentFile(paymentId, "proof", file);
    return rpc("sales_attach_payment_proof", { p_id: paymentId, p_path: path, p_sha256: hash, p_reason: reason || null });
}
// D4: TDS certificate reference and/or file, once. Amounts are never changed.
export async function addTdsCertificate(paymentId, reference, file) {
    const up = file ? await uploadPaymentFile(paymentId, "tds-certificate", file) : null;
    return rpc("sales_add_tds_certificate", { p_id: paymentId, p_reference: reference || null, p_path: up?.path || null, p_sha256: up?.hash || null });
}
// The stored receipt: uploaded under a new name, then recorded once with its hash.
export async function storeReceiptPdf(p, bytes) {
    const { ref, sha256 } = await storeFile({ target: "sales.receipt_pdf", ownerId: p.id, blob: bytes,
        fileName: `${p.receipt_no}.pdf`, mime: "application/pdf",
        legacy: { bucket: BUCKET, path: `receipts/${p.id}/${String(p.receipt_no).replace(/[^A-Za-z0-9-]+/g, "-")}-${newRequestId().slice(0, 8)}.pdf` } });
    return rpc("sales_attach_receipt_pdf", { p_id: p.id, p_path: ref, p_sha256: sha256 });
}
// Any stored payment file, checked against the hash recorded with it. A mismatch is refused: the file is never shown.
export async function openVerifiedFile(path, expectedHash, what) {
    if (!path || !expectedHash) { const e = new Error(`There is no stored ${what}.`); e.code = "INTEGRITY"; throw e; }
    const data = await fileBlob(BUCKET, path);
    const bytes = new Uint8Array(await data.arrayBuffer());
    if (await sha256Hex(bytes) !== expectedHash) {
        const e = new Error(`Integrity check failed: the stored ${what} doesn't match the fingerprint recorded when it was saved, so it won't be opened. Report this to the Admin.`);
        e.code = "INTEGRITY"; throw e;
    }
    return { bytes, type: data.type || "application/octet-stream" };
}
// Statement rows, open items and the client summary: all calculated by the database.
export const clientStatement = (clientId, from, to) => rpc("sales_client_statement_v2", { p_client: clientId, p_from: from, p_to: to });
export const clientOpenItems = (clientId, asOf) => rpc("sales_client_open_items", { p_client: clientId, p_as_of: asOf });
export const clientSummary = async (clientId) => (await rpc("sales_client_summary", { p_client: clientId }))?.[0] || null;

// ─── Sales → Accounts sync (024) ─────────────────────────────────────────────
// Separate from recording: a payment is always saved in Sales first; the sync then posts exactly one Accounts entry, or
// answers "needs attention" with the reason (never a guess). Retrying is always safe (the database allows one entry per payment).
export const syncOverview = () => rpc("sales_sync_overview", {});
export const syncPayment = (paymentId, moneyAccountId) => rpc("sales_sync_payment", { p_payment_id: paymentId, p_money_account_id: moneyAccountId || null });
export const syncCandidates = (paymentId) => rpc("sales_sync_historical_candidates", { p_payment_id: paymentId });
export const syncResolve = ({ paymentId, action, entryId, moneyAccountId, reason, reviewed }) => rpc("sales_sync_historical_resolve", {
    p_payment_id: paymentId, p_action: action, p_entry_id: entryId || null, p_money_account_id: moneyAccountId || null, p_reason: reason, p_candidates_reviewed: !!reviewed });
export const linkClientParty = (clientId, partyId, note) => rpc("sales_sync_link_client", { p_client_id: clientId, p_party_id: partyId, p_note: note || null });
export const createPartyForClient = (clientId) => rpc("sales_sync_create_party", { p_client_id: clientId });
export const setAutoSync = (on, reason) => rpc("sales_sync_set_auto", { p_on: on, p_reason: reason });
